/**
 * RenderSeed - Generates deterministic seeds based on design content
 * 
 * This class creates consistent seed values based on the content being rendered
 * (user images, sticker sets, background images, etc.) so that identical designs
 * produce identical random number sequences.
 */
export class RenderSeed {
    
    /**
     * Generates a deterministic seed from design options
     * 
     * @param {CYODesign} cyoDesign - The design options to generate seed from
     * @returns {number} A deterministic seed value
     */
    static generateSeed(cyoDesign) {
        const components = [];
        
        // Include user image data if available
        if (cyoDesign.userImage) {
            components.push(RenderSeed._getImageHash(cyoDesign.userImage));
        }
        
        // Include transparent image data if available  
        if (cyoDesign.transparentImage) {
            components.push(RenderSeed._getImageHash(cyoDesign.transparentImage));
        }
        
        // Include background image ID if available
        if (cyoDesign.galleryImageID) {
            components.push(RenderSeed._stringToHash(cyoDesign.galleryImageID));
        }
        
        // Include sticker set information
        if (cyoDesign.stickerSet) {
            components.push(RenderSeed._stringToHash(cyoDesign.stickerSet.id));
            
            // Include sticker images if available
            if (cyoDesign.stickerImages && cyoDesign.stickerImages.length > 0) {
                const stickerHashes = cyoDesign.stickerImages.map(img => 
                    RenderSeed._getImageHash(img)
                );
                components.push(RenderSeed._combineHashes(stickerHashes));
            }
        }
        
        // Include placement and style settings that affect randomness
        components.push(RenderSeed._stringToHash(cyoDesign.subjectStyle || ''));
        components.push(RenderSeed._stringToHash(cyoDesign.subjectPlacement || ''));
        components.push(RenderSeed._stringToHash(cyoDesign.stickerPlacement || ''));
        
        // Combine all components into final seed
        return RenderSeed._combineHashes(components);
    }
    
    /**
     * Generates a simple hash from an image element
     * Uses image dimensions and source as a basic fingerprint
     * 
     * @param {HTMLImageElement|HTMLCanvasElement} image - The image to hash
     * @returns {number} Simple hash value
     */
    static _getImageHash(image) {
        if (!image) return 0;
        
        let hash = 0;
        
        // Include dimensions
        hash = RenderSeed._addToHash(hash, image.width || 0);
        hash = RenderSeed._addToHash(hash, image.height || 0);
        
        // Include source if available (for img elements)
        if (image.src) {
            hash = RenderSeed._addToHash(hash, RenderSeed._stringToHash(image.src));
        }
        
        // For canvas elements, try to get some data from the canvas
        if (image instanceof HTMLCanvasElement) {
            try {
                // Sample a few pixels from different locations
                const ctx = image.getContext('2d');
                if (ctx) {
                    const samplePoints = [
                        [0, 0],
                        [Math.floor(image.width / 2), Math.floor(image.height / 2)],
                        [image.width - 1, image.height - 1]
                    ];
                    
                    for (const [x, y] of samplePoints) {
                        if (x < image.width && y < image.height) {
                            const imageData = ctx.getImageData(x, y, 1, 1);
                            const pixel = imageData.data;
                            hash = RenderSeed._addToHash(hash, pixel[0] + pixel[1] * 256 + pixel[2] * 65536 + pixel[3] * 16777216);
                        }
                    }
                }
            } catch (e) {
                // If canvas sampling fails (CORS, etc.), fall back to dimensions only
                // This is fine since dimensions + other content should provide enough uniqueness
            }
        }
        
        return hash;
    }
    
    /**
     * Generates a simple hash from a string
     * 
     * @param {string} str - String to hash
     * @returns {number} Hash value
     */
    static _stringToHash(str) {
        if (!str) return 0;
        
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = RenderSeed._addToHash(hash, char);
        }
        return hash;
    }
    
    /**
     * Combines multiple hash values into a single hash
     * 
     * @param {number[]} hashes - Array of hash values to combine
     * @returns {number} Combined hash value
     */
    static _combineHashes(hashes) {
        let combined = 0;
        for (const hash of hashes) {
            combined = RenderSeed._addToHash(combined, hash);
        }
        return combined;
    }
    
    /**
     * Adds a value to an existing hash using a simple hash function
     * 
     * @param {number} hash - Existing hash value
     * @param {number} value - Value to add to hash
     * @returns {number} New hash value
     */
    static _addToHash(hash, value) {
        hash = ((hash << 5) - hash + value) & 0xffffffff;
        return hash < 0 ? hash + 4294967296 : hash;
    }
    
    /**
     * Creates a human-readable seed identifier for debugging
     * 
     * @param {CYODesign} cyoDesign - The design options
     * @returns {string} Human-readable seed description
     */
    static createSeedDescription(cyoDesign) {
        const parts = [];
        
        if (cyoDesign.userImage) {
            parts.push(`user:${cyoDesign.userImage.width}x${cyoDesign.userImage.height}`);
        }
        
        if (cyoDesign.galleryImageID) {
            parts.push(`bg:${cyoDesign.galleryImageID}`);
        }
        
        if (cyoDesign.stickerSet) {
            parts.push(`stickers:${cyoDesign.stickerSet.id}`);
        }
        
        parts.push(`style:${cyoDesign.subjectStyle}`);
        parts.push(`subj:${cyoDesign.subjectPlacement}`);
        parts.push(`stick:${cyoDesign.stickerPlacement}`);
        
        return parts.join('|');
    }
    
    /**
     * Validates that a seed is reasonable (not zero, within expected range)
     * 
     * @param {number} seed - Seed to validate
     * @returns {boolean} True if seed appears valid
     */
    static isValidSeed(seed) {
        return typeof seed === 'number' && 
               seed > 0 && 
               seed < 4294967296 && 
               !isNaN(seed) && 
               isFinite(seed);
    }
}